<template>
  <div>
    <div>
      <section-title has-line title="Información de Contrato" />
      <div class="row ml-2 mb-5">
        <div class="col">
          <span class="color-secondary-6 font-14 font-weight-normal">
            Contract ID:
          </span>
          <div class="color-black font-16 font-weight-normal">
            {{ contractDetails.contractId | empty }}
          </div>
        </div>
        <div class="col">
          <span class="color-secondary-6 font-14 font-weight-normal">
            Contract Line:
          </span>
          <div class="color-black font-16 font-weight-normal">
            {{ contractDetails.contractLine | empty }}
          </div>
        </div>
        <div class="col">
          <span class="color-secondary-6 font-14 font-weight-normal">
            Distribution Line:
          </span>
          <div class="color-black font-16 font-weight-normal">
            {{ contractDetails.distributionLine | empty }}
          </div>
        </div>
      </div>
      <div>
        <icon-view
          class="mb-3"
          gradient
          icon="fas fa-list"
          icon-background="#FF7058"
          icon-color="#fff"
          title="Información de PO"
        />
        <custom-callout
          v-for="(po, index) in value.purchaseOrders"
          :key="`po-item-${index}`"
          class="mb-2"
        >
          <div class="row py-2">
            <div class="col d-flex align-items-center">
              <span class="color-secondary-6 font-14 font-weight-normal mr-2">
                PO ID:
              </span>
              <span class="color-black font-16 font-weight-bold">
                {{ po.purchaseOrderId }}
              </span>
            </div>
            <div class="col d-flex align-items-center">
              <span class="color-secondary-6 font-14 font-weight-normal mr-2">
                Estatus:
              </span>
              <span class="color-black font-16 font-weight-bold">
                {{ getStatusDescription(po.status) }}
              </span>
            </div>
            <div class="col d-flex justify-content-end align-items-center">
              <span class="color-secondary-6 font-14 font-weight-normal mr-2">
                Merchandise Balance:
              </span>
              <span class="color-black font-16 font-weight-bold color-green">
                {{ po.merchandiseBalance | price }}
              </span>
            </div>
            <div class="col-2 d-flex justify-content-center">
              <span class="color-gray-8 font-14 pr-3 mt-1">
                {{ po.lastUpdatedDate | date }}
              </span>
              <action-button
                v-if="po.status === 'D'"
                class="py-1 px-0 pl-2 d-flex justify-content-center align-items-center"
                i="fas fa-redo-alt pr-2 pt-1"
                variant="outline-success"
                @click="$emit('on-update-po', po.purchaseOrderId)"
              />
            </div>
          </div>
          <template #edition>
            <div class="p-2 mt-1">
              <div
                v-for="(row, i) in po.details"
                :key="`address-item-${i}`"
                class="bg-white rounded p-3 row mb-2 mx-0"
              >
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Line Number:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.lineNumber }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Item ID:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.itemId | empty }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    PO Distribution Line:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.poDistributionLine }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Fund Code:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.fundCode }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Project ID:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.projectId | empty }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Line Status:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.lineStatus | empty }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Merchandise Amount:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.merchandiseAmount | price }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    Merchandise VC:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.merchandiseAmountVC | price }}
                  </div>
                </div>
                <div class="col">
                  <span class="color-secondary-6 font-14 font-weight-normal">
                    DOE Balance Amount:
                  </span>
                  <div class="color-black font-16 font-weight-normal">
                    {{ row.doeBalanceAmount | price }}
                  </div>
                </div>
              </div>
              <custom-result-not-found v-if="po.details.length == 0" />
            </div>
          </template>
        </custom-callout>
        <custom-result-not-found v-if="value.purchaseOrders.length == 0" />
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import IconView from '@/views/provider/billingHistory/components/IconView.vue';
import CustomResultNotFound from 'custom-result-not-found';
import poStatuses from '@/utils/constants/sifdePOStatus';

export default {
  name: 'ContractInfo',
  components: {
    SectionTitle,
    IconView,
    CustomCallout,
    ActionButton,
    CustomResultNotFound,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    contractDetails() {
      return this.value?.contractDetails ?? {};
    },
  },
  methods: {
    getStatusDescription(status) {
      return poStatuses[status] ?? 'No Disponible';
    },
  },
};
</script>
